import {
  FunctionComponent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Alert,
  Typography,
  useTheme,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SyncIcon from "@mui/icons-material/Sync";
import { LockButton } from "@modules/projects/pages/ProjectView/LockButton";

import { ProductOptionCore } from "@modules/product-configuration/components/ProductOption/ProductOption";
import { gql, useQuery, useMutation, toColorInputValue } from "@shared/utils";
import {
  Product,
  ProductConfiguration,
  Takeoff,
} from "@shared/types";
import { ProductConfigurationSelect } from "./ProductConfigurationSelect";
import { useGqlField } from "@shared/hooks";
import { ProductSelect } from "./ProductSelect";
import {useLazyQuery} from '@apollo/client/react';
import { Input } from "../Inputs";
import { ErrorBoundary } from "../ErrorBoundry";
import { TextField } from "../StyledComponents";
import { getTabId } from "@shared/utils/tabId";
import { useAuth } from "@shared/utils/auth";
import { useSettings } from "@providers/settings";

const ACQUIRE_CONFIG_LOCK = gql`
  mutation AcquireConfigLock($takeoffId: String!, $tabId: String!) {
    acquireConfigLock(takeoffId: $takeoffId, tabId: $tabId) {
      success
      message
      lockedBy
      lockedAt
      tabId
    }
  }
`;

const RELEASE_CONFIG_LOCK = gql`
  mutation ReleaseConfigLock($takeoffId: String!, $tabId: String!) {
    releaseConfigLock(takeoffId: $takeoffId, tabId: $tabId)
  }
`;

const FORCE_RELEASE_CONFIG_LOCK = gql`
  mutation ForceReleaseConfigLock($takeoffId: String!) {
    forceReleaseConfigLock(takeoffId: $takeoffId)
  }
`;

const GET_CONFIG_LOCK_STATUS = gql`
  query GetConfigLockStatus($takeoffId: String!) {
    getConfigLockStatus(takeoffId: $takeoffId) {
      isLocked
      tabId
      isExpired
    }
  }
`;

const SET_SYNC_DEFERRED = gql`
  mutation SetTakeoffSyncDeferred($takeoffId: String!, $deferredUntil: Float) {
    setTakeoffSyncDeferred(takeoffId: $takeoffId, deferredUntil: $deferredUntil) {
      _id
      syncDeferredUntil
    }
  }
`;

const CLEAR_NEEDS_INVENTORY_SYNC = gql`
  mutation ClearNeedsInventorySync($takeoffId: String!) {
    clearNeedsInventorySync(takeoffId: $takeoffId) {
      _id
      needsInventorySync
      syncDeferredUntil
    }
  }
`;

const EDIT_PRODUCT_ID = gql`
  mutation EditTakeoff($id: String!, $value: String) {
    updateTakeoff(input: { id: $id, productId: $value }) {
      _id
      productId
      Product {
        name
        _id
      }
    }
  }
`;

const EDIT_PRODUCT_CONFIGURATION_ID = gql`
  mutation EditTakeoff($id: String!, $value: String) {
    updateTakeoff(input: { id: $id, productConfigurationId: $value }) {
      _id
      productConfigurationId
      ProductConfiguration {
        name
        _id
        hidden
      }
    }
  }
`;

const EDIT_NAME = gql`
  mutation EditTakeoff($id: String!, $value: String) {
    updateTakeoff(input: { id: $id, name: $value }) {
      _id
      name
    }
  }
`;
const SYNC_INVENTORY = gql`
  mutation SyncTakeoffMaterialList($id: String!) {
    syncTakeoffMaterialList(input: { id: $id }) {
      _id
    }
  }
`;

const EDIT_LINE_DISTANCE = gql`
  mutation EditTakeoffLinePostDistanceOnCenter($id: String!, $value: Float) {
    updateTakeoff(input: { id: $id, linePostDistanceOnCenter: $value }) {
      _id
      linePostDistanceOnCenter
    }
  }
`;

const EDIT_CORNER_DISTANCE = gql`
  mutation EditTakeoffCornerPostDistanceOnCenter($id: String!, $value: Float) {
    updateTakeoff(input: { id: $id, cornerPostDistanceOnCenter: $value }) {
      _id
      cornerPostDistanceOnCenter
    }
  }
`;

export const TAKEOFF_DATA = gql`
  query Takeoff($id: String!) {
    takeoff(id: $id) {
      _id
      locked
      name
      productId
      color
      takeoffId
      needsInventorySync
      Product {
        name
        _id
      }
      productConfigurationId
      cornerPostDistanceOnCenter
      linePostDistanceOnCenter
      ProductConfiguration {
        name
        hidden
        _id
      }
      ParentTakeoff {
        _id
        name
        productId
        productConfigurationId
      }
    }
  }
`;

const Queries = {
  subTakeoffs: gql`
    query Takeoff($takeoffId: String!) {
      takeoff(id: $takeoffId) {
        SubTakeoffs {
          _id
          name
        }
      }
    }
  `,
  cloneTakeoff: gql`
    query CloneTakeoff($id: ID!, $cloningTakeoffId: String!) {
      cloneTakeoff(id: $id, cloningTakeoffId: $cloningTakeoffId) {
        _id
      }
    }
  `,
};

export type TakeoffData = {
  takeoff?: Pick<
    Takeoff,
    | "_id"
    | "productId"
    | "productConfigurationId"
    | "name"
    | "cornerPostDistanceOnCenter"
    | "linePostDistanceOnCenter"
  > & {
    takeoffId?: string;
    locked?: boolean;
    needsInventorySync?: boolean;
    Product: Pick<Product, "name" | "_id">;
    ProductConfiguration: Pick<ProductConfiguration, "name" | "_id" | "hidden">;
    color?: string;
    ParentTakeoff?: Pick<
      Takeoff,
      "_id" | "name" | "productId" | "productConfigurationId"
    >;
  };
};

export const TakeoffEditModal: FunctionComponent<{
  onClose?: () => any;
  fullScreen?: boolean;
  takeoffId: string;
  updateTakeoffColor: (...args: any) => Promise<any>;
  isSubTakeoff?: boolean;
}> = ({
  takeoffId,
  onClose,
  fullScreen,
  updateTakeoffColor: updateTakeoffColorMutation,
  isSubTakeoff,
}) => {
  const theme = useTheme();
  const takeoffIdReady = Boolean(takeoffId?.trim());
  const [tab, setTab] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const takeoffData = useQuery<TakeoffData>(TAKEOFF_DATA, {
    variables: { id: takeoffId },
    skip: !takeoffIdReady,
  });
  const [showCloneSubTakeoffConfirmation, setShowCloneSubTakeoffConfirmation] =
    useState<string | undefined>(undefined);
  const [isCloning, setIsCloning] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [allSubTakeoffsQuery, { data: subTakeoffsData }] = useLazyQuery<{
    takeoff: { SubTakeoffs: { _id: string; name: string }[] };
  }>(Queries.subTakeoffs);

  const [cloneTakeoffQuery] = useLazyQuery(Queries.cloneTakeoff);

  const subTakeoffsList = useMemo(
    () =>
      subTakeoffsData?.takeoff?.SubTakeoffs?.filter(
        (s) => s._id !== takeoffId
      ) || [],
    [subTakeoffsData?.takeoff, takeoffId]
  );

  const [productConfigurationId, setProductConfigurationId, productConfigurationIdUtils] = useGqlField(
    takeoffData?.data?.takeoff?.productConfigurationId,
    takeoffId,
    EDIT_PRODUCT_CONFIGURATION_ID
  );

  const [lineDistance, setLineDistanceRaw, lineDistanceUtils] = useGqlField(
    takeoffData?.data?.takeoff?.linePostDistanceOnCenter || 0,
    takeoffId,
    EDIT_LINE_DISTANCE
  );
  // EDIT_CORNER_DISTANCE
  const [cornerDistance, setCornerDistanceRaw, cornerDistanceUtils] = useGqlField(
    takeoffData?.data?.takeoff?.cornerPostDistanceOnCenter || 0,
    takeoffId,
    EDIT_CORNER_DISTANCE
  );

  // Validation state for distance fields
  const [lineDistanceError, setLineDistanceError] = useState<string | null>(null);
  const [cornerDistanceError, setCornerDistanceError] = useState<string | null>(null);

  // Validation helper for distance fields
  const validateDistance = (value: number | string | null): { isValid: boolean; error: string | null } => {
    if (value === null || value === '' || value === undefined) {
      return { isValid: false, error: 'Value is required' };
    }
    const numVal = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(numVal) || !Number.isFinite(numVal)) {
      return { isValid: false, error: 'Must be a valid number' };
    }
    if (numVal < 0.1) {
      return { isValid: false, error: 'Must be at least 0.1' };
    }
    if (numVal > 10000) {
      return { isValid: false, error: 'Must be 10,000 or less' };
    }
    return { isValid: true, error: null };
  };

  // Wrapped setters with validation - only mutate if valid
  const setLineDistance = useCallback((val: number | string | null) => {
    const validation = validateDistance(val);
    setLineDistanceError(validation.error);
    if (validation.isValid) {
      setLineDistanceRaw(typeof val === 'string' ? parseFloat(val) : val);
    }
  }, [setLineDistanceRaw]);

  const setCornerDistance = useCallback((val: number | string | null) => {
    const validation = validateDistance(val);
    setCornerDistanceError(validation.error);
    if (validation.isValid) {
      setCornerDistanceRaw(typeof val === 'string' ? parseFloat(val) : val);
    }
  }, [setCornerDistanceRaw]);

  const [name, setName, nameUtils] = useGqlField(
    takeoffData?.data?.takeoff?.name,
    takeoffId,
    EDIT_NAME
  );
  const [productId, setProductId, productIdUtils] = useGqlField(
    takeoffData?.data?.takeoff?.productId,
    takeoffId,
    EDIT_PRODUCT_ID
  );

  const updateTakeoffColor = (color?: string) => {
    if (color === "") return;
    updateTakeoffColorMutation &&
      updateTakeoffColorMutation({
        variables: {
          id: takeoffId,
          color,
        },
      });
  };

  const [syncInventory, { loading: syncLoading }] = useMutation(SYNC_INVENTORY);
  const [setSyncDeferred] = useMutation(SET_SYNC_DEFERRED);
  const [clearNeedsInventorySync] = useMutation(CLEAR_NEEDS_INVENTORY_SYNC);
  const [acquireConfigLockMutation] = useMutation<{ acquireConfigLock: { success: boolean; message?: string; lockedBy?: string; lockedAt?: string; tabId?: string } }>(ACQUIRE_CONFIG_LOCK);
  const [releaseConfigLockMutation] = useMutation(RELEASE_CONFIG_LOCK);
  const [forceReleaseConfigLockMutation] = useMutation(FORCE_RELEASE_CONFIG_LOCK);

  // CONFIG LOCK: prevent concurrent editing across tabs/windows
  const LOCK_DURATION_MS = 30 * 60 * 1000; // 30 minutes
  const tabId = getTabId();
  const { token } = useAuth();
  const { getSetting } = useSettings();
  const [lockStatus, setLockStatus] = useState<'acquiring' | 'acquired' | 'denied'>('acquiring');
  const [lockFailedInfo, setLockFailedInfo] = useState<{ message: string; lockedBy?: string } | null>(null);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const lockAcquiredAtRef = useRef<number | null>(null);
  const [remoteCloseMsg, setRemoteCloseMsg] = useState<string | null>(null);
  const [isForcingTakeover, setIsForcingTakeover] = useState(false);

  // Force takeover: release existing lock and acquire it ourselves
  const handleForceTakeover = useCallback(async () => {
    if (!takeoffId) return;
    setIsForcingTakeover(true);
    try {
      await forceReleaseConfigLockMutation({ variables: { takeoffId } });
      const result = await acquireConfigLockMutation({ variables: { takeoffId, tabId } });
      const data = result.data?.acquireConfigLock;
      if (data?.success) {
        setLockStatus('acquired');
        lockAcquiredAtRef.current = Date.now();
        setRemainingMs(LOCK_DURATION_MS);
        setLockFailedInfo(null);
      } else {
        // Still failed - show error
        setLockFailedInfo({
          message: data?.message || 'Failed to acquire lock',
          lockedBy: data?.lockedBy,
        });
      }
    } catch {
      // Network error - allow anyway
      setLockStatus('acquired');
      lockAcquiredAtRef.current = Date.now();
      setRemainingMs(LOCK_DURATION_MS);
    } finally {
      setIsForcingTakeover(false);
    }
  }, [takeoffId, tabId, forceReleaseConfigLockMutation, acquireConfigLockMutation, LOCK_DURATION_MS]);

  // SYNC DEFERRAL: 5 minute max deferral for safety
  const DEFER_DURATION_MS = 15 * 60 * 1000; // 15 minutes - gives user time for phone calls etc

  // Helper to set sync deferral (with timestamp-based safety)
  const deferSync = useCallback(() => {
    if (takeoffId) {
      setSyncDeferred({
        variables: {
          takeoffId,
          deferredUntil: Date.now() + DEFER_DURATION_MS,
        },
      });
    }
  }, [takeoffId, setSyncDeferred]);

  // Helper to clear sync deferral - returns promise for awaiting
  const clearSyncDefer = useCallback(async () => {
    if (takeoffId) {
      await setSyncDeferred({
        variables: {
          takeoffId,
          deferredUntil: null,
        },
      });
    }
  }, [takeoffId, setSyncDeferred]);

  // Mutations for reverting fields on Cancel
  const [revertName] = useMutation(EDIT_NAME);
  const [revertLineDistance] = useMutation(EDIT_LINE_DISTANCE);
  const [revertCornerDistance] = useMutation(EDIT_CORNER_DISTANCE);
  const [revertProductId] = useMutation(EDIT_PRODUCT_ID);
  const [revertProductConfigurationId] = useMutation(EDIT_PRODUCT_CONFIGURATION_ID);

  const locked = takeoffData?.data?.takeoff?.locked || false;

  // Change tracking: snapshot values once when data first loads
  const originalValues = useRef<{
    name?: string;
    productId?: string;
    productConfigurationId?: string;
    lineDistance: number;
    cornerDistance: number;
  } | null>(null);
  const isInitialized = useRef(false);

  // Keep refs to the latest useGqlField values so the deferred capture reads
  // post-settle state rather than stale closure values.
  const nameRef = useRef(name);
  const productIdRef = useRef(productId);
  const productConfigurationIdRef = useRef(productConfigurationId);
  const lineDistanceRef = useRef(lineDistance);
  const cornerDistanceRef = useRef(cornerDistance);
  nameRef.current = name;
  productIdRef.current = productId;
  productConfigurationIdRef.current = productConfigurationId;
  lineDistanceRef.current = lineDistance;
  cornerDistanceRef.current = cornerDistance;

  const [optionsChanged, setOptionsChanged] = useState(false);
  const revertOptionsRef = useRef<(() => Promise<void>) | null>(null);

  // Track locally whether any mutations have fired since modal opened.
  // This is independent of the backend's needsInventorySync flag, which
  // can be cleared by the background sync job while the modal is open.
  const [hasUnsyncedChanges, setHasUnsyncedChanges] = useState(false);
  
  // Called IMMEDIATELY when user interacts with any option (before mutation fires)
  // This makes the UI update instantly
  const onOptionsChange = useCallback(() => {
    setOptionsChanged(true);
    setHasUnsyncedChanges(true);
  }, []);

  // Set hasUnsyncedChanges IMMEDIATELY when any field value changes from original.
  // This makes the UI update instantly rather than waiting for debounced mutations.
  // We compare current values to original values (captured on mount).
  useEffect(() => {
    if (!originalValues.current) return;
    const orig = originalValues.current;
    const anyChanged = 
      name !== orig.name ||
      productId !== orig.productId ||
      productConfigurationId !== orig.productConfigurationId ||
      lineDistance !== orig.lineDistance ||
      cornerDistance !== orig.cornerDistance ||
      optionsChanged;
    
    if (anyChanged) {
      setHasUnsyncedChanges(true);
    }
  }, [
    name,
    productId,
    productConfigurationId,
    lineDistance,
    cornerDistance,
    optionsChanged,
  ]);

  useEffect(() => {
    originalValues.current = null;
    isInitialized.current = false;
    setOptionsChanged(false);
    setHasUnsyncedChanges(false);
    // Reset lock state when switching takeoffs
    lockAcquiredAtRef.current = null;
    setLockStatus('acquiring');
    setRemoteCloseMsg(null);
    setLockFailedInfo(null);
    setRemainingMs(null);
  }, [takeoffId]);

  // CONFIG LOCK: Acquire lock when modal opens
  useEffect(() => {
    if (!takeoffId) return;
    setLockStatus('acquiring');
    acquireConfigLockMutation({ variables: { takeoffId, tabId } })
      .then((result) => {
        const data = result.data?.acquireConfigLock;
        if (data?.success) {
          setLockStatus('acquired');
          lockAcquiredAtRef.current = Date.now();
          setRemainingMs(LOCK_DURATION_MS);
        } else {
          setLockStatus('denied');
          setLockFailedInfo({
            message: data?.message || 'Configuration is being edited by another session',
            lockedBy: data?.lockedBy,
          });
          // Don't auto-close - let user choose to force takeover or cancel
        }
      })
      .catch(() => {
        // Graceful degradation: allow usage even if lock fails
        setLockStatus('acquired');
        lockAcquiredAtRef.current = Date.now();
        setRemainingMs(LOCK_DURATION_MS);
      });
    return () => {
      // Release lock on unmount as safety backup
      releaseConfigLockMutation({ variables: { takeoffId, tabId } }).catch(() => {});
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeoffId]);

  // CONFIG LOCK: release on tab/window close. sendBeacon can't carry the
  // auth header and the app routes through GraphQL — use fetch with keepalive
  // (same pattern as SearchTakeoffView's edit-lock release).
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);
  const graphqlEndpointRef = useRef(getSetting('GRAPHQL_ENDPOINT'));
  graphqlEndpointRef.current = getSetting('GRAPHQL_ENDPOINT');
  useEffect(() => {
    if (lockStatus !== 'acquired' || !takeoffId) return;
    const handleUnload = () => {
      const endpoint = graphqlEndpointRef.current;
      const authToken = tokenRef.current;
      if (!endpoint) return;
      const body = JSON.stringify({
        query: 'mutation ReleaseConfigLock($takeoffId: String!, $tabId: String!) { releaseConfigLock(takeoffId: $takeoffId, tabId: $tabId) }',
        variables: { takeoffId, tabId },
      });
      try {
        fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
          },
          body,
          keepalive: true,
        }).catch(() => {/* page is going away */});
      } catch {/* page is going away */}
    };
    window.addEventListener('pagehide', handleUnload);
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('pagehide', handleUnload);
      window.removeEventListener('beforeunload', handleUnload);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockStatus, takeoffId]);

  // CONFIG LOCK: Countdown timer
  const handleSyncAndCloseRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    handleSyncAndCloseRef.current = handleSyncAndClose;
  });
  useEffect(() => {
    if (lockStatus !== 'acquired') return;
    const interval = setInterval(() => {
      if (!lockAcquiredAtRef.current) return;
      const elapsed = Date.now() - lockAcquiredAtRef.current;
      const remaining = Math.max(0, LOCK_DURATION_MS - elapsed);
      setRemainingMs(remaining);
      if (remaining === 0) {
        clearInterval(interval);
        handleSyncAndCloseRef.current?.();
      }
    }, 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockStatus]);

  // CONFIG LOCK: Poll own lock to detect force-release by another tab
  const { data: ownLockData } = useQuery<{
    getConfigLockStatus: { isLocked: boolean; tabId?: string; isExpired: boolean };
  }>(GET_CONFIG_LOCK_STATUS, {
    variables: { takeoffId },
    skip: !takeoffIdReady || lockStatus !== 'acquired',
    pollInterval: 3 * 1000,
    fetchPolicy: 'network-only',
  });
  useEffect(() => {
    if (!ownLockData) return;
    // Only check for remote close if we've actually acquired the lock
    // (lockAcquiredAtRef is set when acquire succeeds)
    if (!lockAcquiredAtRef.current) return;
    // Suppress checks for a brief grace window right after acquire. Apollo's
    // normalized cache may serve a stale snapshot written by another query
    // (e.g. SearchTakeoffView's poll) that still shows the previous holder —
    // without this guard, a force-takeover would immediately self-close the
    // modal. Same pattern as the edit-lock detection in SearchTakeoffView.
    const POST_ACQUIRE_GRACE_MS = 7_000;
    if (Date.now() - lockAcquiredAtRef.current < POST_ACQUIRE_GRACE_MS) return;
    const lock = ownLockData.getConfigLockStatus;
    // If lock is gone or held by a different tab, we were force-released
    if (!lock.isLocked || (lock.tabId && lock.tabId !== tabId)) {
      setRemoteCloseMsg('Configuration was closed remotely');
      setTimeout(() => {
        handleSyncAndCloseRef.current?.();
      }, 1500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownLockData]);

  // SYNC DEFERRAL: Set deferral when modal opens (takeoffId becomes truthy)
  useEffect(() => {
    if (takeoffId) {
      deferSync();
    }
    // Cleanup: clear deferral when component unmounts or takeoffId changes
    return () => {
      if (takeoffId) {
        // Clear the deferral on unmount as a safety backup
        setSyncDeferred({
          variables: {
            takeoffId,
            deferredUntil: null,
          },
        });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeoffId]);

  useEffect(() => {
    if (isInitialized.current || !takeoffData.data?.takeoff) return;

    // useGqlField initializes via useState(fieldValue) on first render, then
    // updates its internal state via useEffect when fieldValue arrives. Capture
    // after a short delay so those effects have settled before we snapshot.
    const timer = setTimeout(() => {
      originalValues.current = {
        name: nameRef.current,
        productId: productIdRef.current,
        productConfigurationId: productConfigurationIdRef.current,
        lineDistance: lineDistanceRef.current,
        cornerDistance: cornerDistanceRef.current,
      };
      isInitialized.current = true;
    }, 100);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeoffData.data?.takeoff]);

  const orig = originalValues.current;
  const nameChanged = !!orig && name !== orig.name;
  const productIdChanged = !!orig && productId !== orig.productId;
  const productConfigurationIdChanged = !!orig && productConfigurationId !== orig.productConfigurationId;
  const lineDistanceChanged = !!orig && lineDistance !== orig.lineDistance;
  const cornerDistanceChanged = !!orig && cornerDistance !== orig.cornerDistance;
  const hasChanges = nameChanged || productIdChanged || productConfigurationIdChanged || lineDistanceChanged || cornerDistanceChanged || optionsChanged;
  
  // Check if any mutations are in-flight (isDirty = local value differs from server value)
  const savingInProgress = 
    nameUtils.isDirty || 
    productIdUtils.isDirty || 
    productConfigurationIdUtils.isDirty || 
    lineDistanceUtils.isDirty || 
    cornerDistanceUtils.isDirty;
  
  // Use local hasUnsyncedChanges state for UI decisions, NOT the backend's needsInventorySync.
  // The backend flag can be cleared by the background sync job while modal is open.
  // hasUnsyncedChanges is set whenever any mutation fires and stays true until modal closes.
  const needsSync = hasUnsyncedChanges || hasChanges;

  // Helper to trigger sync and close - called by both button and backdrop click
  const handleSyncAndClose = useCallback(async () => {
    // Release config lock (backend sets needsInventorySync=true and clears syncDeferredUntil)
    await releaseConfigLockMutation({ variables: { takeoffId, tabId } }).catch(() => {});
    // Clear sync deferral as backup
    clearSyncDefer();
    // Trigger an explicit inventory sync so the BOM updates immediately
    // instead of waiting for the 15-second backend poller.
    if (hasUnsyncedChanges) {
      syncInventory({ variables: { id: takeoffId } }).catch(() => {});
    }
    onClose?.();
  }, [takeoffId, tabId, onClose, clearSyncDefer, releaseConfigLockMutation, hasUnsyncedChanges, syncInventory]);

  const changedBorderSx = (isChanged: boolean) =>
    isChanged
      ? {
          "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.info.main,
            borderWidth: "2px",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.info.main,
          },
        }
      : {};

  const editableOptions =
    takeoffData?.data?.takeoff?.ProductConfiguration?.hidden === true && !takeoffData?.data?.takeoff?.locked;
  const open = takeoffId !== "";

  /** Ref on the main dialog Paper — see focusTakeoffDialogSurface. */
  const dialogPaperRef = useRef<HTMLDivElement | null>(null);

  /**
   * Chrome warns when MUI sets aria-hidden on #root while focus is still on a
   * control under #root (open/close timing). Nudge focus into the dialog after
   * enter, and use closeAfterTransition={false} so aria-hidden is cleared before
   * focus restores on close (mui#43106).
   */
  const focusTakeoffDialogSurface = useCallback(() => {
    requestAnimationFrame(() => {
      const paper = dialogPaperRef.current;
      if (!paper?.isConnected) return;
      if (paper.contains(document.activeElement)) return;
      const candidate = paper.querySelector<HTMLElement>(
        "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])",
      );
      (candidate ?? paper).focus({ preventScroll: true });
    });
  }, []);

  const onSubTakeoffSelect = (takeoffId: string) => {
    setShowCloneSubTakeoffConfirmation(takeoffId);
    handleClose();
  };

  useEffect(() => {
    if (!isSubTakeoff) return;
    const t = takeoffData?.data?.takeoff;
    if (!t) return;
    const parentTakeoffId =
      (typeof t.takeoffId === "string" ? t.takeoffId.trim() : "") ||
      (typeof t.ParentTakeoff?._id === "string" ? t.ParentTakeoff._id.trim() : "");
    if (!parentTakeoffId) return;
    void allSubTakeoffsQuery({
      variables: { takeoffId: parentTakeoffId },
    });
  }, [
    isSubTakeoff,
    takeoffData?.data?.takeoff?.takeoffId,
    takeoffData?.data?.takeoff?.ParentTakeoff?._id,
    allSubTakeoffsQuery,
  ]);

  const onCloneSubCategory = async () => {
    const cloningTakeoffId = showCloneSubTakeoffConfirmation;
    // Close dialog immediately, show loading state
    setShowCloneSubTakeoffConfirmation(undefined);
    setIsCloning(true);
    try {
      await cloneTakeoffQuery({
        variables: {
          id: takeoffId,
          cloningTakeoffId,
        },
      });
      await takeoffData.refetch();
    } finally {
      setIsCloning(false);
    }
  };

  return (
    <>
      <Dialog
        PaperProps={{
          sx: { width: theme.breakpoints.values.xl },
          tabIndex: -1,
          ref: dialogPaperRef,
        }}
        closeAfterTransition={false}
        TransitionProps={{ onEntered: focusTakeoffDialogSurface }}
        onClose={() => {
          // Trigger sync when closing via backdrop/escape
          handleSyncAndClose();
        }}
        open={open}
        fullScreen={fullScreen}
        maxWidth="xl"
        sx={{ backgroundColor: theme.palette.background.default }}
      >
        <DialogTitle
          sx={{
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: 16 }}>
              {takeoffData.data?.takeoff?.name}
            </Typography>
            {hasChanges && !isCloning && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "warning.main" }}>
                <WarningAmberIcon fontSize="small" />
                <Typography sx={{ fontSize: 14, fontWeight: "bold" }}>Changes Made</Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {takeoffData?.data?.takeoff?.ParentTakeoff?._id && (
              <Button color="primary" variant="contained" onClick={handleClick} disabled={locked}>
                Clone From Sub Takeoffs
              </Button>
            )}
            {takeoffData?.data?.takeoff && (
              <LockButton
                takeoff={{
                  _id: takeoffData.data.takeoff._id,
                  locked: takeoffData.data.takeoff.locked ?? false,
                  needsInventorySync: needsSync,
                }}
                onChange={() => takeoffData.refetch()}
              />
            )}
          </Box>
          <Menu  open={menuOpen} anchorEl={anchorEl} onClose={handleClose}>
            {subTakeoffsList?.map((tk) => (
              <MenuItem
                key={tk._id}
                value={tk._id}
                onClick={() => onSubTakeoffSelect(tk._id)}
              >
                {tk.name}
              </MenuItem>
            ))}
          </Menu>
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: theme.palette.background.default,
            position: 'relative',
          }}
        >
          <ErrorBoundary>
            {lockStatus === 'acquiring' ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 3 }}>
                <CircularProgress size={20} />
                <Typography>Checking configuration access...</Typography>
              </Box>
            ) : lockStatus === 'denied' ? (
              <Box sx={{ p: 3 }}>
                <Typography color="error" fontWeight="bold">Configuration Locked</Typography>
                <Typography sx={{ mt: 1 }}>{lockFailedInfo?.message}</Typography>
                {lockFailedInfo?.lockedBy && (
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                    Locked by user: {lockFailedInfo.lockedBy}
                  </Typography>
                )}
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    onClick={onClose}
                    disabled={isForcingTakeover}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="contained" 
                    color="warning"
                    onClick={handleForceTakeover}
                    disabled={isForcingTakeover}
                    startIcon={isForcingTakeover ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    {isForcingTakeover ? 'Taking over...' : 'Force Takeover'}
                  </Button>
                </Box>
                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                  Force takeover will close the other session's lock and give you control.
                </Typography>
              </Box>
            ) : !takeoffData?.data ? (
              <Box sx={{ display: "flex" }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Cloning overlay - fixed position so it stays centered while scrolling */}
                {isCloning && (
                  <Box
                    sx={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      zIndex: 9999,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={80} sx={{ color: 'white' }} />
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Cloning Configuration...
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Please wait while the configuration is being copied
                    </Typography>
                  </Box>
                )}
                {remoteCloseMsg && (
                  <Box sx={{ px: 3, pt: 2 }}>
                    <Alert severity="info">{remoteCloseMsg}</Alert>
                  </Box>
                )}
                <Grid container>
                <Grid item xs={4}>
                  <Paper sx={{ padding: 2, marginTop: 2 }}>
                    <Input
                     disabled={locked}
                      sx={{
                        marginTop: 2,
                        marginBottom: 2,
                        ...changedBorderSx(nameChanged),
                      }}
                      fullWidth
                      value={name}
                      onChange={(val) => {
                        setName(val);
                      }}
                      label="Name"
                    />
                    <Input
                     disabled={locked}
                      sx={{
                        marginTop: 2,
                        marginBottom: 2,
                        color : !lineDistance ? "warning" : "primary",
                        ...changedBorderSx(lineDistanceChanged),
                      }}
                      fullWidth
                      error={!lineDistance || !!lineDistanceError}
                      helperText={lineDistanceError}
                      value={lineDistance}
                      onChange={( val) => {
                        //const val = parseFloat(ce.target.value);
                        if (val || val === 0) setLineDistance(val as string | number);
                      }}
                      type="number"
                      label="Line Post Distance"
                      inputProps={{ min: 0.1, max: 10000, step: 0.1 }}
                    />
                    <Input
                     disabled={locked}
                      sx={{
                        marginTop: 2,
                        marginBottom: 2,
                        ...changedBorderSx(cornerDistanceChanged),
                      }}
                      fullWidth
                      error={!cornerDistance || !!cornerDistanceError}
                      helperText={cornerDistanceError}
                      value={cornerDistance}
                      onChange={(val) => {
                        if (val || val === 0) setCornerDistance(val as string | number);
                      }}
                      type="number"
                      label="Corner Post Distance"
                      inputProps={{ min: 0.1, max: 10000, step: 0.1 }}
                    />
                    <Input
                    disabled={locked}
                      type="color"
                      onChange={(value) => {
                        if (typeof value === "string") updateTakeoffColor(value);
                      }}
                      value={toColorInputValue(takeoffData?.data?.takeoff?.color)}
                      textFieldProps={{
                        onBlur: (event) => {
                          updateTakeoffColor(event.target.value);
                        },
                        label: "Takeoff Color",
                        fullWidth: true,
                        sx: {
                          marginTop: 2,
                          marginBottom: 2,
                        },
                      }}
                    />
                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                      Product & configuration
                    </Typography>
                    <Box
                      sx={{ borderRadius: 1, ...changedBorderSx(productIdChanged) }}
                    >
                      <ProductSelect
                        disabled={locked}
                        productId={productId}
                        parentProductId={
                          takeoffData?.data.takeoff?.ParentTakeoff?.productId
                        }
                        onChange={(d) => {
                          setProductId(d);
                        }}
                      />
                    </Box>
                    {productId ? (
                      <Box
                        sx={{
                          borderRadius: 1,
                          ...changedBorderSx(productConfigurationIdChanged),
                        }}
                      >
                        <ProductConfigurationSelect
                          parentProductConfigurationId={
                            takeoffData?.data?.takeoff?.ParentTakeoff
                              ?.productConfigurationId
                          }
                          disabled={locked}
                          name={takeoffData.data?.takeoff?.name || ""}
                          productId={productId}
                          productConfigurationId={productConfigurationId}
                          onChange={(d) => {
                            setProductConfigurationId(d);
                          }}
                        />
                      </Box>
                    ) : null}
                  </Paper>
                </Grid>
                <Grid item xs={8}>
                  <ProductOptionCore
                    {...{
                      tab,
                      takeoffId,
                      setTab: setTab as any,
                      showAdd,
                      setShowAdd,
                      productConfiguration: productConfigurationId,
                      readonly: !editableOptions || locked,
                      onOptionsChange,
                      revertOptionsRef,
                      parentProductConfigurationId:
                        takeoffData?.data?.takeoff?.ParentTakeoff
                          ?.productConfigurationId,
                    }}
                  />
                </Grid>
                </Grid>
              </>
            )}
          </ErrorBoundary>
        </DialogContent>
        <DialogActions
          sx={{
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}
        >
          {lockStatus === 'acquired' && remainingMs !== null && (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.primary.contrastText, mr: 'auto', opacity: remainingMs < 5 * 60 * 1000 ? 1 : 0.6 }}
            >
              Session expires in {Math.floor(remainingMs / 60000)}:{String(Math.floor((remainingMs % 60000) / 1000)).padStart(2, '0')}
            </Typography>
          )}
          {hasChanges && (
            <Button
              disabled={isCloning}
              onClick={async () => {
                // Release config lock on cancel
                await releaseConfigLockMutation({ variables: { takeoffId, tabId } }).catch(() => {});
                // CRITICAL: Clear sync deferral on cancel - await to prevent race
                await clearSyncDefer();
                const orig = originalValues.current;
                if (orig) {
                  // CRITICAL: First cancel all pending debounced mutations to prevent race conditions.
                  // Without this, the debounce could fire AFTER our revert mutations, overwriting them.
                  nameUtils.cancelAndReset(orig.name);
                  lineDistanceUtils.cancelAndReset(orig.lineDistance);
                  cornerDistanceUtils.cancelAndReset(orig.cornerDistance);
                  productIdUtils.cancelAndReset(orig.productId);
                  productConfigurationIdUtils.cancelAndReset(orig.productConfigurationId);
                  // Clear any validation errors on cancel
                  setLineDistanceError(null);
                  setCornerDistanceError(null);

                  // Now revert any changes that were already persisted to the backend
                  const revertPromises: Promise<any>[] = [];
                  if (nameChanged) {
                    revertPromises.push(revertName({ variables: { id: takeoffId, value: orig.name } }));
                  }
                  if (lineDistanceChanged) {
                    revertPromises.push(revertLineDistance({ variables: { id: takeoffId, value: orig.lineDistance } }));
                  }
                  if (cornerDistanceChanged) {
                    revertPromises.push(revertCornerDistance({ variables: { id: takeoffId, value: orig.cornerDistance } }));
                  }
                  if (productIdChanged) {
                    revertPromises.push(revertProductId({ variables: { id: takeoffId, value: orig.productId } }));
                  }
                  if (productConfigurationIdChanged) {
                    revertPromises.push(revertProductConfigurationId({ variables: { id: takeoffId, value: orig.productConfigurationId } }));
                  }
                  await Promise.all(revertPromises);
                }
                // Revert right panel options
                await revertOptionsRef.current?.();
                // Clear needsInventorySync since we reverted to original values - no sync needed
                await clearNeedsInventorySync({ variables: { takeoffId } });
                // Refetch to update local state with reverted values
                await takeoffData.refetch();
                onClose?.();
              }}
              variant="outlined"
              color="secondary"
              sx={{ color: theme.palette.primary.contrastText, borderColor: theme.palette.primary.contrastText }}
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={handleSyncAndClose}
            variant="contained"
            color="secondary"
            disabled={syncLoading || savingInProgress || isCloning}
            startIcon={(syncLoading || savingInProgress) ? <CircularProgress size={16} color="inherit" /> : needsSync ? <SyncIcon /> : undefined}
          >
            {syncLoading ? "Syncing..." : savingInProgress ? "Saving..." : needsSync ? "Close and Sync" : "Close"}
          </Button>
          {editableOptions && (
            <Button
              onClick={() => setShowAdd(true)}
              variant="contained"
              color="secondary"
              disabled={isCloning}
            >
              Add Component
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!showCloneSubTakeoffConfirmation}
        onClose={() => setShowCloneSubTakeoffConfirmation(undefined)}
        closeAfterTransition={false}
      >
        <DialogTitle>Confirm Cloning</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to clone this specification?
          </Typography>
          <Typography sx={{ fontWeight: "bold" }}>
            This will delete existing configuration
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCloneSubTakeoffConfirmation(undefined)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onCloneSubCategory}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
